import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useOnboarding } from '../../../context/OnboardingContext';
import { PrivateWizardLayout, WizardContainer, WizardCard, WizardActions } from '../WizardLayout';
import Stepper from '../Stepper';
import { toast } from 'react-toast';
import { LuPencil, LuArrowLeft } from 'react-icons/lu';
import { WizardSelect } from '../onboarding';
import axiosInstance from '../../../api/axiosInstance';
import '../../../styles/wizard.css';
import { ROLE_OPTIONS, STEP_MODE, STEP_STATUS } from '../../../Config/Onboarding/constant';
import { isWizardCompletedUser } from '../../../utils/authRedirectUtils';

const Step2Workspace = () => {
  const navigate = useNavigate();
  const { 
    engine, 
    data, 
    nextStep, 
    previousStep, 
    setWorkspace, 
    setStepStatus, 
    setStepMode,
    setStepEditing,
    setStepCreatedEntityId,
    markStepCompleted,
    state
  } = useOnboarding();
  
  const stepId = 2;
  const stepState = engine.steps[stepId];
  const workspaceData = data.workspace;
  
  // Determine step statuses
  const isCreating = stepState?.status === STEP_STATUS.IN_PROGRESS;
  const isEditMode = stepState?.status === STEP_STATUS.COMPLETED && stepState?.mode === STEP_MODE.EDIT;
  const isEditing = stepState?.isEditing === true;
  
  // Inputs should be disabled only when NOT in CREATE or NOT in EDITING mode
  const isFieldsDisabled = !isCreating && !isEditing;

  // Wizard-completed users start here and cannot go back to intent selection (Step 1)
  const isWizardCompleted = isWizardCompletedUser();

  const [formData, setFormData] = useState({
    name: workspaceData.name || '',
    role: workspaceData.role || ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Auto-populate workspace name from user data on first load
  useEffect(() => {
    if (!formData.name && isCreating) {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (user.username) {
        const domain = user.username.includes('@') 
          ? user.username.split('@')[1].split('.')[0] 
          : user.username;
        const autoName = domain.charAt(0).toUpperCase() + domain.slice(1) + ' Workspace';
        setFormData(prev => ({
          ...prev,
          name: autoName
        }));
      }
    }
  }, [isCreating]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Workspace name is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
      const saveWizardData = async () => {
      try {
        await axiosInstance.post('/wizard/add_wizard_data', {
          wizard_data: {
            engine: engine,
            data: state.data
          }
        });
      } catch (error) {
        console.warn('[Step 6B] Warning: Failed to save wizard data:', error.message);
      }
    };

  const handleSaveAndNext = async () => {
    if (!validate()) return;

    setIsLoading(true);
    try {
      // Call API: workspace/add_workspace with query params
const response = await axiosInstance.post(
  `/workspace/add_workspace?workspace_name=${formData.name}&role=${formData.role ? parseInt(formData.role) : ''}&is_wizard=true`
);

      if (response.status === 200 && response.workspace_id) {
        // Save workspace data
        setWorkspace({
          ...formData,
          id: response.workspace_id
        });

        // Update step state: mark as completed, set mode to edit, set entity ID
        setStepCreatedEntityId(stepId, response.workspace_id);
        //  setStepStatus(1, STEP_STATUS.COMPLETED);
        markStepCompleted(stepId);
        // setStepStatus(stepId, STEP_STATUS.COMPLETED);
        
        // Mark step 3 as in_progress
        setStepStatus(3, STEP_STATUS.IN_PROGRESS);

        toast.success('Workspace created successfully');
        
        // Move to next step
        nextStep();
        saveWizardData();
        navigate('/app/onboarding?step=3');
      }
    } catch (error) {
      console.error('Error creating workspace:', error);
      toast.error(error.response?.data?.detail?.message || 'Failed to create workspace. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditClick = () => {
    setStepEditing(stepId, true);
  };

  const handleUpdateWorkspace = async () => {
    if (!validate()) return;

    setIsLoading(true);
    try {
      // Call API: workspace/update_workspace with body payload
      const response = await axiosInstance.put('/workspace/update_workspace', {
        workspace_id: workspaceData.id,
        workspace_name: formData.name,
        role: formData.role ? parseInt(formData.role) : null,
        is_wizard: true
      });

      if (response.status === 200) {
        setWorkspace(formData);
        setStepEditing(stepId, false);
        toast.success('Workspace updated successfully');
      }
    } catch (error) {
      console.error('Error updating workspace:', error);
      toast.error(error.response?.data?.message || 'Failed to update workspace. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: workspaceData.name,
      role: workspaceData.role
    });
    setStepEditing(stepId, false);
  };

  const handleContinue = () => {
    nextStep();
    navigate('/app/onboarding?step=3');
  };

  const handleBack = () => {
    previousStep();
    navigate('/app/onboarding?step=1');
  };

  return (
    <PrivateWizardLayout>
      <WizardContainer>
        <Stepper currentStep={2} completedSteps={engine.flow.completedSteps} />
        
        <WizardCard
          title="Create your workspace"
          subtitle="Your workspace is where your team manages projects, tests and results together."
        >
          {/* Edit/Update Header */}
          {isEditMode && !isEditing && (
            <div className="wizard-workspace-edit-header">
              <div></div>
              <button
                onClick={handleEditClick}
                className="wizard-workspace-edit-btn"
                title="Edit workspace settings"
              >
                <LuPencil size={16} />
                Edit
              </button>
            </div>
          )}

          {isEditMode && isEditing && (
            <div className="wizard-workspace-edit-header">
              <div></div>
              <div className="wizard-workspace-edit-actions">
                <button
                  className="wizard-workspace-edit-btn secondary"
                  onClick={handleCancel}
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  className="wizard-workspace-edit-btn primary"
                  onClick={handleUpdateWorkspace}
                  disabled={!formData.name.trim() || isLoading}
                >
                  {isLoading ? 'Updating...' : 'Update'}
                </button>
              </div>
            </div>
          )}

          {/* Form - Two Column Layout with Equal Width */}
          <div className="wizard-workspace-form">
            <div className="wizard-workspace-form-group">
              <label htmlFor="ob-ws-name">
                Workspace Name <span className="required">*</span>
              </label>
              <input
                id="ob-ws-name"
                type="text"
                name="name"
                aria-invalid={Boolean(errors.name) || undefined}
                aria-describedby="ob-ws-name-help"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Acme Corp Workspace"
                disabled={isFieldsDisabled || isLoading}
              />
              {errors.name && <p className="error">{errors.name}</p>}
              <p id="ob-ws-name-help">Visible to everyone in your team</p>
            </div>

            <div className="wizard-workspace-form-group">
              <label id="ob-ws-role-label" htmlFor="ob-ws-role">
                Your Role <span className="required">*</span>
              </label>
              <WizardSelect
                id="ob-ws-role"
                name="role"
                value={formData.role}
                onChange={handleChange}
                disabled={isFieldsDisabled || isLoading}
                aria-labelledby="ob-ws-role-label"
                aria-describedby="ob-ws-role-help"
              >
                <option value="">Select your role</option>
                {ROLE_OPTIONS.map(role => (
                  <option key={role.value} value={role.value}>
                    {role.label}
                  </option>
                ))}
              </WizardSelect>
              <p id="ob-ws-role-help">Helps us tailor your setup</p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="wizard-workspace-actions">
            {!isWizardCompleted && (
              <button
                type="button"
                className="back"
                onClick={handleBack}
                disabled={isLoading}
              >
                <LuArrowLeft size={16} aria-hidden="true" />
                Back
              </button>
            )}

            {isCreating ? (
              <button
                className="primary"
                onClick={handleSaveAndNext}
                disabled={!formData.name.trim() || !formData.role || isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="ob-spinner" aria-hidden="true" />
                    Creating...
                  </>
                ) : (
                  'Save & Next'
                )}
              </button>
            ) : (
              <button
                className="primary"
                onClick={handleContinue}
              >
                Continue
              </button>
            )}
          </div>
        </WizardCard>
      </WizardContainer>
    </PrivateWizardLayout>
  );
};

export default Step2Workspace;